import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Trash2, Edit2, Server as ServerIcon, RefreshCw } from 'lucide-react';
import { Server } from '../../types';

interface ServerListProps {
  servers: Server[];
  setEditingServer: (server: Server) => void;
  handleDeleteServer: (id: string) => void;
  toggleCredentials: (serverId: string) => void;
  showCredentials: { [key: string]: boolean };
  onRegenerateCredentials: (serverId: string) => void;
  onGenerateBillingToken: (server: Server) => void;
  generatingTokenServerId: string | null;
}

const ServerList: React.FC<ServerListProps> = ({
  servers = [],
  setEditingServer,
  handleDeleteServer,
  toggleCredentials,
  showCredentials,
  onRegenerateCredentials,
  onGenerateBillingToken,
  generatingTokenServerId,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {servers.map(server => (
        <div key={server.id} className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ServerIcon className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold">{server.name}</h3>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={`/servers/${server.id}`}
                className="text-slate-600 hover:text-slate-900"
                title="Server details"
              >
                Details
              </Link>
              <button
                onClick={() => setEditingServer(server)}
                className="text-blue-600 hover:text-blue-800"
                title="Edit server"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDeleteServer(server.id)}
                className="text-red-600 hover:text-red-800"
                title="Delete server"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <div>
              <span className="text-sm text-gray-600">Host:</span>
              <p className="text-sm font-mono break-all">{server.host}</p>
            </div>

            <div className="mt-4">
              <button
                onClick={() => onGenerateBillingToken(server)}
                className="w-full rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={generatingTokenServerId === server.id}
              >
                {generatingTokenServerId === server.id
                  ? 'Generating billing token...'
                  : 'Generate BILLING_BACKEND_BEARER_TOKEN'}
              </button>
              <p className="mt-2 text-xs text-gray-500">
                Generate a new website bearer token for this source URL. The token is shown only once.
              </p>
            </div>

            {server.api_key && server.api_secret && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">API Credentials</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onRegenerateCredentials(server.id)}
                      className="text-orange-600 hover:text-orange-800"
                      title="Regenerate credentials"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => toggleCredentials(server.id)}
                      className="text-blue-600 hover:text-blue-800"
                      title={showCredentials[server.id] ? "Hide credentials" : "Show credentials"}
                    >
                      {showCredentials[server.id] ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {showCredentials[server.id] && (
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-gray-600">API Key:</span>
                      <p className="text-xs font-mono bg-gray-100 p-1 rounded break-all">
                        {server.api_key}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-600">API Secret:</span>
                      <p className="text-xs font-mono bg-gray-100 p-1 rounded break-all">
                        {server.api_secret}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between mt-4">
              <span className={`text-xs px-2 py-1 rounded ${
                server.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {server.is_active ? 'Active' : 'Inactive'}
              </span>
              <span className="text-xs text-gray-500">
                Created: {new Date(server.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ServerList;
